import Select from 'react-select';

// Radix-themed react-select styles
const customSelectStyles = {
  control: (provided, state) => ({
    ...provided,
    borderRadius: 'var(--radius-3)',
    borderColor: state.isFocused ? 'var(--accent-9)' : 'var(--gray-7)',
    boxShadow: state.isFocused ? '0 0 0 1px var(--accent-9)' : 'none',
    minHeight: '40px',
    '&:hover': {
      borderColor: 'var(--gray-8)',
    },
  }),
  valueContainer: provided => ({
    ...provided,
    padding: '1px 12px',
  }),
  input: provided => ({
    ...provided,
    margin: '0',
    padding: '0',
    '& input': {
      margin: '0 !important',
    },
  }),
  multiValue: provided => ({
    ...provided,
    backgroundColor: 'var(--accent-3)',
    borderRadius: 'var(--radius-2)',
  }),
  multiValueLabel: provided => ({
    ...provided,
    color: 'var(--accent-11)',
    padding: '1px 6px',
  }),
  multiValueRemove: provided => ({
    ...provided,
    color: 'var(--accent-11)',
    ':hover': {
      backgroundColor: 'var(--accent-4)',
      color: 'var(--accent-12)',
    },
  }),
  menu: provided => ({
    ...provided,
    borderRadius: 'var(--radius-3)',
    boxShadow: 'var(--shadow-5)',
    border: '1px solid var(--gray-6)',
  }),
  option: (provided, state) => ({
    ...provided,
    fontSize: '14px',
    fontWeight: 500,
    backgroundColor: state.isSelected
      ? 'var(--accent-9)'
      : state.isFocused
      ? 'var(--gray-3)'
      : 'transparent',
    color: state.isSelected ? 'white' : 'var(--gray-12)',
    cursor: 'pointer',
    ':active': {
      backgroundColor: 'var(--accent-9)',
    },
  }),
  placeholder: provided => ({
    ...provided,
    color: 'var(--gray-9)',
  }),
  menuPortal: provided => ({
    ...provided,
    zIndex: 9999,
    fontFamily: "'Montserrat', sans-serif",
  }),
};

/**
 * RadixSelect - A wrapper around react-select with Radix UI theming
 *
 * This component provides consistent Radix UI styling for react-select dropdowns
 * throughout the application. It supports all react-select props including
 * single/multi-select, searchable, clearable, etc.
 *
 * @example
 * // Basic usage
 * <RadixSelect
 *   options={[{ value: 1, label: 'Option 1' }]}
 *   onChange={handleChange}
 *   placeholder="Select an option..."
 * />
 *
 * @example
 * // Multi-select
 * <RadixSelect
 *   isMulti
 *   options={options}
 *   value={selectedValues}
 *   onChange={setSelectedValues}
 * />
 */
const RadixSelect = ({ styles, ...props }) => {
  // Merge custom styles with Radix styles, allowing overrides
  const mergedStyles = styles
    ? [
        ...new Set([
          ...Object.keys(customSelectStyles),
          ...Object.keys(styles),
        ]),
      ].reduce((acc, key) => {
        acc[key] = (provided, state) => {
          const base = customSelectStyles[key];
          const override = styles[key];
          const customStyle =
            typeof base === 'function' ? base(provided, state) : base || {};
          const userStyle =
            typeof override === 'function'
              ? override(provided, state)
              : override || {};
          return { ...customStyle, ...userStyle };
        };
        return acc;
      }, {})
    : customSelectStyles;

  return <Select styles={mergedStyles} {...props} />;
};

export default RadixSelect;
